import React, { useState, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { EngineIcon, engineLabels, getDisplayDateNumeric, getDisplayTime } from 'Utils';
import { Button, Spinner } from 'react-bootstrap';
import { BsFillCircleFill, BsGearFill, BsPlayFill } from 'react-icons/bs';
import { FaSave, FaPlus } from 'react-icons/fa';
import { saveDataSource } from 'api';
import AppContext from 'Utils/AppContext';
import { sourceIconsAsElements, getDataSourceTypeIcon } from 'SensitiveDataLandscape/LandscapeUtils';
import { kindForSource } from 'AssetManager/LandscapeUtils';
import { managerSourceToEditorPrefill, encodePrefill } from './managerSources';
import ConnectionOptionsModal from './ConnectionOptionsModal';
import DiscoveryTooltip from 'DiscoveryTooltip';

const SettingsButton = ({ setShow, modified }) => {
  return (
    <Button
      variant='outline-secondary rounded d-flex justify-content-center'
      onClick={() => setShow(true)}
      style={{ width: '40px', paddingBottom: '5px' }}
    >
      <div>
        <BsGearFill className='ml-1 mr-1' />
      </div>
      { modified && (
        <div>
          <BsFillCircleFill size='5px' />
        </div>
      )}
    </Button>
  )
}

const ActionButton = ({ data }) => {
  const [fetchingData, setFetchingData] = useState(false);
  const [error, setError] = useState(null);
  const { loadConnectionsHandler, startSearchHandler, defaultRules } = useContext(AppContext);
  const runSearch = data.runSearch;
  const ButtonIcon = runSearch ? BsPlayFill : FaSave;
  const { user, source, database, port, connections, connectionName } = data;
  const existingDataSourceName = connectionName || (connections && connections[0]);
  const hasExistingDataSource = Boolean(existingDataSourceName);
  // RDBMs and servers need connection details before a discovery can run; gate by category
  // so cloud-specific keys (azure_sql, gcp_sql, …) are covered, not just the AWS 'rds'/'ec2'.
  const requiresConnectionFields = kindForSource(source) === 'rdbm' || kindForSource(source) === 'servers';
  const disabled = requiresConnectionFields && (!hasExistingDataSource && (!user || !database || !port));
  let tooltip = 'Discover data on this source.';

  if (disabled) tooltip = 'Fill in the required fields to run a Discovery on this source.';

  if (hasExistingDataSource) {
    tooltip = `A Data Source named ${existingDataSourceName} currently exists for this data source. Click to run a Discovery on this Data Source.`;
  }



  const handleAction = async (data) => {
    const dataValue = { ...data };
    setFetchingData(true);
    try {
      if (hasExistingDataSource) {
        dataValue.name = existingDataSourceName;
      } else {
        await saveDataSource(dataValue);
      }

      if (runSearch) {
        const timestamp = Date.now();
        const dataSourceName = existingDataSourceName || dataValue.name || data.connectionName || 'Data Source';
        const discoveryLabel = `${dataSourceName} ${getDisplayDateNumeric(timestamp)} @ ${getDisplayTime(timestamp)}`;
        await startSearchHandler(dataValue, defaultRules, discoveryLabel);
      }
      await loadConnectionsHandler();
    } catch(e) {
      console.log(e);
      setError(e.message);
    }
    setFetchingData(false);
  }


  return (
    <DiscoveryTooltip text={tooltip} placement='top'>
      <Button
        disabled={(fetchingData || disabled)}
        variant='primary rounded d-flex justify-content-center'
        onClick={async () => await handleAction(data)}
      >
        <div>
          { fetchingData ? (
            <Spinner size='sm' animation='border' className='ml-1 mr-1' />
          ) : (
            <ButtonIcon color='#fff' className='ml-1 mr-1' />
          )}
        </div>
      </Button>
    </DiscoveryTooltip>
  )
}

const datasourceTypeLabelMap = {
  RELATIONAL: 'RELATIONAL',
  FILE_FOLDER: 'FILE FOLDER',
  NOSQL: 'NOSQL',
};

const SourceColumn = ({ source, datasourceType }) => (
  <div className='text-uppercase'>
    <span className='icon-span mr-1'>
      {getDataSourceTypeIcon(datasourceType) || sourceIconsAsElements[source] || sourceIconsAsElements.external}
    </span>
    <span className='icon-span' >{datasourceTypeLabelMap[datasourceType] || source}</span>
  </div>
);

const EngineColumn = ({ engine }) => {
  return (
    <div className='text-uppercase'>
      <div className='d-flex align-items-center'>
        { engine ? (
          <span>
            <span className='icon-span mr-1'><EngineIcon engine={engine} /></span>
            <span className='text-span'>{engineLabels[engine]}</span>
          </span>
        ) : (
          <span>-</span>
        )}
      </div>
    </div>
  )
}

// Manager-surfaced potential source: a single button that opens the Source Editor with
// the resource's known fields pre-filled. No save/run here — the user reviews and saves
// (and optionally runs discovery) from the editor.
const CreateDataSourceButton = ({ data }) => {
  const history = useHistory();
  const handleCreate = () => {
    const prefill = encodePrefill(managerSourceToEditorPrefill(data.resource || {}));
    history.push(`/sources/source-editor?prefill=${prefill}`);
  };

  return (
    <DiscoveryTooltip text='Create a data source from this detected resource.' placement='top'>
      <Button
        variant='primary rounded d-flex justify-content-center align-items-center'
        onClick={handleCreate}
      >
        <FaPlus color='#fff' className='mr-1' size='12px' />
        <span className='text-white'>Add</span>
      </Button>
    </DiscoveryTooltip>
  );
};

const ActionsColumn = ({ data }) => {
  const [show, setShow] = useState(null);
  const modified = data.modified;

  if (data._potential) {
    return (
      <div className='d-flex justify-content-center w-100 align-items-center action-btns-col'>
        <CreateDataSourceButton data={data} />
      </div>
    );
  }

  return (
    <div className='d-flex justify-content-center w-100 align-items-center action-btns-col'>
      <SettingsButton modified={modified} setShow={setShow} />
      <ActionButton data={data} />

      <ConnectionOptionsModal
        { ...data }
        show={show}
        setShow={setShow}
      />
    </div>
  )
}

export {
  ActionsColumn,
  ActionButton,
  EngineColumn,
  SourceColumn,
  SettingsButton
}